import { useState, type FormEvent } from "react";
import { api } from "../api/client";
import type { Category, Transaction, TxKind } from "../api/types";
import { useErrorHandler } from "../auth/AuthContext";
import {
  formatCents,
  isoFromLocalParts,
  localPartsFromIso,
  parseDollarsToCents,
  tzLabel,
} from "../lib/money";
import { ErrorMessage, Field, Modal, Segmented } from "./common";

type Direction = "in" | "out";
type Timing = "now" | "scheduled";

/**
 * Whether an entry follows the child's Spend/Save split instead of naming a
 * bucket. Mirrors `splitsAcrossBuckets` on the server, which is the authority —
 * this only decides what the form shows.
 *
 * Deposits and allowances are money arriving, and where arriving money lands is
 * the child's call. An adjustment is excluded even though it can be positive: it
 * corrects the record rather than giving money, so it has to be able to name the
 * bucket it is correcting.
 */
function splitsAcrossBuckets(kind: TxKind, direction: Direction): boolean {
  return direction === "in" && (kind === "deposit" || kind === "allowance");
}

/**
 * Divides an amount the way the server will, so the form can show the outcome
 * before it happens. Integer cents only, remainder to Spend — the same
 * arithmetic as `model.SplitForChild`.
 */
function previewSplit(
  cents: number,
  savePercent: number,
): { save: number; spend: number } {
  const save = Math.floor((cents * savePercent) / 100);
  return { save, spend: cents - save };
}

/** True when an ISO timestamp falls on today's date in the family's timezone. */
function isToday(iso: string): boolean {
  const then = localPartsFromIso(iso).date;
  if (!then) return true;
  return then === localPartsFromIso(new Date().toISOString()).date;
}

/** Today's date in the family's timezone, for the picker's default. */
function todayInput(): string {
  return localPartsFromIso(new Date().toISOString()).date;
}

/**
 * Where arriving money will land, shown in place of a bucket picker.
 *
 * The parent decides how much arrives; the child decides how it divides. This
 * says so plainly rather than leaving the parent to wonder where their deposit
 * went — and once an amount is typed, it shows the actual figures.
 */
function SplitNote({
  childName,
  savePercent,
  cents,
}: {
  childName: string;
  savePercent: number;
  cents: number | null;
}) {
  const preview = cents !== null && cents > 0 ? previewSplit(cents, savePercent) : null;

  return (
    <div className="split-note">
      <p className="split-note__head">
        {savePercent > 0
          ? `${childName} saves ${savePercent}% of money that comes in.`
          : `${childName} puts all incoming money into Spend.`}
      </p>

      {preview ? (
        <dl className="split-note__rows">
          <div>
            <dt>
              <span className="split-note__dot split-note__dot--save" />
              Save
            </dt>
            <dd>{formatCents(preview.save)}</dd>
          </div>
          <div>
            <dt>
              <span className="split-note__dot split-note__dot--spend" />
              Spend
            </dt>
            <dd>{formatCents(preview.spend)}</dd>
          </div>
        </dl>
      ) : null}

      <p className="split-note__foot">
        {childName} chooses this — you decide how much, they decide where it
        goes.
      </p>
    </div>
  );
}

/**
 * Add or edit a transaction.
 *
 * The form asks for a positive amount plus a direction, and converts to the
 * signed cents the API expects on submit. Asking a parent to type a minus sign
 * is a needless way to record the wrong number.
 */
export function TransactionModal({
  childId,
  childName,
  childSavePercent,
  categories,
  existing,
  onClose,
  onSaved,
}: {
  childId: string;
  childName: string;
  /** The child's chosen share of arriving money, for the split preview. */
  childSavePercent: number;
  categories: Category[];
  existing: Transaction | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toMessage = useErrorHandler();

  const [direction, setDirection] = useState<Direction>(
    existing ? (existing.amountCents < 0 ? "out" : "in") : "in",
  );
  const [amount, setAmount] = useState(
    existing ? formatCents(Math.abs(existing.amountCents)).replace("$", "") : "",
  );
  const [kind, setKind] = useState<TxKind>(existing?.kind ?? "deposit");
  // Money always lands in a bucket, so this defaults to the first one rather
  // than to an empty "no bucket" choice. The server enforces the same rule.
  const [categoryId, setCategoryId] = useState(
    existing?.categoryId ?? categories.find((c) => !c.archived)?.id ?? "",
  );
  const [note, setNote] = useState(existing?.note ?? "");
  const [date, setDate] = useState(
    () => localPartsFromIso(existing?.occurredAt ?? new Date().toISOString()).date,
  );
  const [time, setTime] = useState(
    () => localPartsFromIso(existing?.occurredAt ?? new Date().toISOString()).time,
  );
  // "Now" vs "Pick a date". An existing entry opens on whichever it already is,
  // so editing a scheduled transaction does not silently reset it to today.
  const [timing, setTiming] = useState<Timing>(() =>
    existing && !isToday(existing.occurredAt) ? "scheduled" : "now",
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // The instant the picker currently describes, used for both the hint and the
  // EDT/EST label — which must be read at the scheduled moment, not now, or
  // scheduling into January from July would say "EDT".
  const scheduledIso = isoFromLocalParts(date, time);

  // Kind options depend on direction so an impossible pairing (a "deposit" that
  // takes money out) can't be selected in the first place.
  const kindOptions: { value: TxKind; label: string }[] =
    direction === "in"
      ? [
          { value: "deposit", label: "Deposit" },
          { value: "allowance", label: "Allowance" },
          { value: "adjustment", label: "Adjustment" },
        ]
      : [
          { value: "withdrawal", label: "Spending" },
          { value: "adjustment", label: "Adjustment" },
        ];

  function changeDirection(next: Direction) {
    setDirection(next);
    // Keep the kind consistent with the new direction.
    const stillValid = (next === "in" ? ["deposit", "allowance", "adjustment"] : ["withdrawal", "adjustment"]).includes(kind);
    if (!stillValid) setKind(next === "in" ? "deposit" : "withdrawal");
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");

    const magnitude = parseDollarsToCents(amount);
    if (magnitude === null || magnitude === 0) {
      setError("Enter an amount, like 5.00");
      return;
    }
    if (magnitude < 0) {
      setError("Enter a positive amount and pick Money in or Money out.");
      return;
    }

    // Money arriving carries no bucket — the split decides — and the server
    // refuses one sent with it.
    const split = splitsAcrossBuckets(kind, direction);
    if (!split && !categoryId) {
      setError("Pick a bucket — Spend or Save.");
      return;
    }

    const signed = direction === "out" ? -magnitude : magnitude;
    // "Now" sends no date and lets the server stamp it, so the entry is not
    // accidentally dated by a client whose clock is off.
    const occurredAt =
      timing === "now" ? undefined : isoFromLocalParts(date, time);
    if (timing === "scheduled" && !occurredAt) {
      setError("Pick a date for this one.");
      return;
    }

    setSaving(true);
    try {
      if (existing) {
        // An edit still names a bucket: these rows already exist in one, and
        // moving money between them is a correction rather than an arrival.
        await api.updateTransaction(existing.id, {
          amountCents: signed,
          kind,
          note,
          categoryId,
          ...(occurredAt ? { occurredAt } : {}),
        });
      } else {
        await api.createTransaction(childId, {
          amountCents: signed,
          kind,
          note,
          ...(split ? {} : { categoryId }),
          ...(occurredAt ? { occurredAt } : {}),
        });
      }
      onSaved();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={existing ? "Edit transaction" : `Add money for ${childName}`}
      onClose={onClose}
    >
      <form className="stack" onSubmit={submit}>
        <ErrorMessage>{error}</ErrorMessage>

        <Segmented<Direction>
          value={direction}
          onChange={changeDirection}
          options={[
            { value: "in", label: "Money in" },
            { value: "out", label: "Money out" },
          ]}
        />

        <Field label="Amount" hint="For example 5 or 12.50">
          <input
            className="input"
            inputMode="decimal"
            autoFocus
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>

        <Field label="Type">
          <select
            className="select"
            value={kind}
            onChange={(e) => setKind(e.target.value as TxKind)}
          >
            {kindOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        {splitsAcrossBuckets(kind, direction) && !existing ? (
          <SplitNote
            childName={childName}
            savePercent={childSavePercent}
            cents={parseDollarsToCents(amount)}
          />
        ) : (
          <Field
            label="Bucket"
            {...(kind === "adjustment"
              ? { hint: "A correction names the bucket it's fixing." }
              : {})}
          >
            <select
              className="select"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              {categories
                .filter((c) => !c.archived)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </Field>
        )}

        <Field label="Note">
          <input
            className="input"
            placeholder="What was it for?"
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>

        <Field label="When">
          <Segmented<Timing>
            value={timing}
            onChange={(next) => {
              setTiming(next);
              // Opening the picker on today is a better starting point than
              // whatever date was last left in the field.
              if (next === "scheduled" && !date) setDate(todayInput());
            }}
            options={[
              { value: "now", label: "Now" },
              { value: "scheduled", label: "Pick a date" },
            ]}
          />
        </Field>

        {timing === "scheduled" ? (
          <Field
            label="Date and time"
            hint={
              scheduledIso && new Date(scheduledIso).getTime() > Date.now()
                ? `Scheduled — it won't count until then (${tzLabel(scheduledIso)}).`
                : `Recorded right away (${tzLabel(scheduledIso)}).`
            }
          >
            <div className="row row--fields">
              <input
                className="input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              <input
                className="input"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
          </Field>
        ) : null}

        <div className="row row--between">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : existing ? "Save changes" : "Add it"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
